import { useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { db, reference } from '../lib/localdb.js';
import { addProspect } from '../lib/actions.js';
import { capture } from '../lib/media.js';
import { BigButton, CameraButton, Chip } from '../components/Controls.js';

/**
 * Prospecting from the van.
 *
 * A technician standing in a driveway can see the neighbour's outdoor unit, its brand, and
 * roughly its age. In a trade where replacement is the high-ticket sale, that is a qualified
 * lead nobody is currently writing down.
 *
 * **It must cost under fifteen seconds or it will not happen between two jobs**, which is the
 * only real constraint on this screen: one photograph, an address prefilled from the stop just
 * left, and everything else optional.
 *
 * **A prospect is a place, not a person.** No name, no telephone number, nobody's personal
 * information — none of that is visible from a driveway, and guessing it would be both wrong
 * and unlawful. The photograph is taken from public property and shows the equipment and the
 * house; never a person, a licence plate, or anything behind a fence.
 */
export function ProspectScreen() {
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [line1, setLine1] = useState('');
  const [city, setCity] = useState('');
  const [brand, setBrand] = useState('');
  const [age, setAge] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [mediaId, setMediaId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      const settings = await reference<{ prospectingEnabled?: boolean }>('settings', {});
      setEnabled(Boolean(settings.prospectingEnabled));

      // Prefilled from the last stop, because the street is almost always the same one and
      // typing an address between two jobs is the thing that stops this happening at all.
      const stops = await (await db()).getAll('stops');
      const recent = stops
        .filter((stop) => stop.address)
        .sort((a, b) => (b.arrivedAt ?? b.startsAt ?? '').localeCompare(a.arrivedAt ?? a.startsAt ?? ''))[0];
      if (recent?.address) {
        setLine1(recent.address.line1);
        setCity(recent.address.city);
      }
    })();
  }, []);

  // A trade whose opportunities are not visible from the street never sees this control.
  if (enabled === false) {
    return (
      <div className="empty">
        <p>La prospection n’est pas activée pour cette entreprise.</p>
        <Link to="/" className="empty__action">
          Retour
        </Link>
      </div>
    );
  }

  return (
    <div className="prospect">
      <header className="stopscreen__head">
        <Link to="/" className="back" aria-label="Retour">
          ‹
        </Link>
        <div className="stopscreen__title">
          <h1>Observation</h1>
          <p>Un appareil vu de la rue</p>
        </div>
      </header>

      <CameraButton
        tone={mediaId ? 'quiet' : 'primary'}
        onFile={async (file) => {
          // Not attached to a stop: an observation belongs to a place, and the place may have
          // no job of any kind.
          const saved = await capture({ appointmentId: '', kind: 'prospect', blob: file });
          setMediaId(saved.id);
        }}
      >
        {mediaId ? '✓ Photo prise' : 'Photographier l’appareil et la maison'}
      </CameraButton>

      <label className="field">
        <span>Adresse</span>
        <input value={line1} onChange={(event) => setLine1(event.target.value)} placeholder="300 Rue Fernand-Casavant" />
      </label>
      <label className="field">
        <span>Ville</span>
        <input value={city} onChange={(event) => setCity(event.target.value)} />
      </label>
      <label className="field">
        <span>Marque</span>
        <input value={brand} onChange={(event) => setBrand(event.target.value)} placeholder="Carrier, Lennox…" />
      </label>

      <h4>Âge apparent</h4>
      <div className="chips">
        {[5, 10, 15, 20].map((years) => (
          <Chip key={years} on={age === years} onToggle={() => setAge(years)}>
            {years} ans et plus
          </Chip>
        ))}
      </div>

      <label className="field">
        <span>Note</span>
        <textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Base rouillée" />
      </label>

      <BigButton
        disabled={line1.trim().length < 3 || city.trim().length < 2}
        busy={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await addProspect({
              line1: line1.trim(),
              city: city.trim(),
              brand: brand.trim() || null,
              apparentAgeYears: age,
              note: note.trim() || null,
              mediaId,
            });
            void navigate({ to: '/' });
          } finally {
            setBusy(false);
          }
        }}
        hint="Aucune donnée personnelle n’est enregistrée"
      >
        Enregistrer l’observation
      </BigButton>
    </div>
  );
}
